import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './ProtectiveCoveredCall.css';

// Strategy globals (editable in code)
const MIN_DTE = 9;
const MAX_DTE = 45;
const TARGET_ANNUAL_RETURN = 1.0; // 100%
const PROTECTION_TARGETS_PCT = [10, 15, 20];

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatMoney(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  return `$${value.toFixed(2)}`;
}

function formatPct(value, digits = 2) {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

function nearestBy(items, getVal, target) {
  if (!Array.isArray(items) || items.length === 0) return null;
  let best = null;
  let bestDiff = Infinity;
  for (const it of items) {
    const v = getVal(it);
    if (v == null) continue;
    const diff = Math.abs(v - target);
    if (diff < bestDiff) {
      best = it;
      bestDiff = diff;
    }
  }
  return best;
}

function pickThreeExpirations(exps) {
  // exps expected sorted ascending by days_to_expiration
  if (!Array.isArray(exps) || exps.length === 0) return [];

  const first = exps[0];
  const last = exps[exps.length - 1];
  if (!first || !last) return [];

  if (first.expiration_date === last.expiration_date) {
    return [first];
  }

  const firstDte = toNumber(first.days_to_expiration);
  const lastDte = toNumber(last.days_to_expiration);
  if (firstDte == null || lastDte == null) {
    // Fallback: return unique by expiration_date (up to 3)
    const uniq = [];
    const seen = new Set();
    for (const e of exps) {
      if (!e?.expiration_date) continue;
      if (seen.has(e.expiration_date)) continue;
      seen.add(e.expiration_date);
      uniq.push(e);
      if (uniq.length >= 3) break;
    }
    return uniq;
  }

  const midTarget = (firstDte + lastDte) / 2;
  const mid = nearestBy(exps, (e) => toNumber(e.days_to_expiration), midTarget) || first;

  // Deduplicate but preserve order: first, mid, last
  const out = [];
  const seen = new Set();
  for (const e of [first, mid, last]) {
    const key = e?.expiration_date || `${e?.days_to_expiration ?? ''}`;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

function ProtectiveCoveredCall() {
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [raw, setRaw] = useState(null);

  const run = async () => {
    const sym = (symbol || '').trim().toUpperCase();
    if (!sym) {
      setError('Enter a ticker symbol.');
      return;
    }

    setLoading(true);
    setError(null);
    setRaw(null);
    try {
      // Fetch FULL call chain from marketdata.app via backend.
      const res = await axios.post(`${API_URL}/portfolio/protective-covered-call/chain/`, { symbol: sym });
      setRaw(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const results = useMemo(() => {
    if (!raw) return { rows: [], chosenAnnualReturnPct: null, stockPrice: null, symbol: null };

    const stockPrice = toNumber(raw.stock_price);
    const sym = raw.symbol || (symbol || '').trim().toUpperCase();
    if (stockPrice == null) {
      return { rows: [], chosenAnnualReturnPct: null, stockPrice: null, symbol: sym };
    }

    const expirations = Array.isArray(raw.expirations) ? raw.expirations : [];
    const filtered = expirations
      .map((e) => ({
        ...e,
        days_to_expiration: toNumber(e.days_to_expiration),
      }))
      .filter((e) => e.days_to_expiration != null && e.days_to_expiration > MIN_DTE && e.days_to_expiration < MAX_DTE)
      .sort((a, b) => a.days_to_expiration - b.days_to_expiration);

    const picked = pickThreeExpirations(filtered);
    const deepItmStrikeTarget = stockPrice * 0.85; // informational: 15% ITM reference

    const rows = [];
    for (const exp of picked) {
      const dte = toNumber(exp.days_to_expiration);
      const calls = Array.isArray(exp.calls) ? exp.calls : [];
      const cleanCalls = calls
        .map((c) => ({
          ...c,
          strike: toNumber(c.strike),
          premium: toNumber(c.premium),
        }))
        .filter((c) => c.strike != null && c.premium != null)
        .sort((a, b) => a.strike - b.strike);

      for (const protectionTargetPct of PROTECTION_TARGETS_PCT) {
        const maxStrikeToMeetProtection = stockPrice * (1 - (protectionTargetPct / 100));

        const candidates = cleanCalls.filter((c) => c.strike <= maxStrikeToMeetProtection);
        const desiredStrike = maxStrikeToMeetProtection; // strike that yields exactly target protection

        // Pick the closest listed strike to the protection threshold while still meeting it.
        // If none meet it (rare), fall back to closest overall strike and flag it.
        const chosen = nearestBy(
          candidates.length > 0 ? candidates : cleanCalls,
          (c) => c.strike,
          desiredStrike
        );

        const strike = chosen ? chosen.strike : null;
        const premium = chosen ? chosen.premium : null;
        const breakeven = premium != null ? stockPrice - premium : null;
        const netCapitalOutlay = breakeven; // same number; kept internal for clarity in formulas
        const maxProfit = (strike != null && netCapitalOutlay != null) ? (strike - netCapitalOutlay) : null;
        const rocPct = (maxProfit != null && netCapitalOutlay) ? (maxProfit / netCapitalOutlay) * 100 : null;
        const protectionPct = strike != null ? ((stockPrice - strike) / stockPrice) * 100 : null;
        const annualizedReturnPct = (rocPct != null && dte) ? (rocPct / dte) * 365 : null;

        const protectionMet = protectionPct != null ? (protectionPct >= protectionTargetPct - 1e-9) : false;
        const highRisk = protectionPct != null ? (protectionPct < 10) : false;

        rows.push({
          expiration_date: exp.expiration_date || '—',
          dte,
          stockPrice,
          deepItmStrikeTarget,
          protectionTargetPct,
          desiredStrike,
          maxStrikeToMeetProtection,
          strike,
          premium,
          breakeven,
          maxProfit,
          rocPct,
          protectionPct,
          annualizedReturnPct,
          protectionMet,
          highRisk,
        });
      }
    }

    const targetAnnualPct = TARGET_ANNUAL_RETURN * 100;
    // Prefer highlighting among rows that meet their protection target.
    const eligible = rows.filter((r) => r.protectionMet && r.annualizedReturnPct != null);
    const best = nearestBy((eligible.length > 0 ? eligible : rows), (r) => r.annualizedReturnPct, targetAnnualPct);
    const chosenAnnualReturnPct = best?.annualizedReturnPct ?? null;

    return { rows, chosenAnnualReturnPct, stockPrice, symbol: sym, targetAnnualPct };
  }, [raw, symbol]);

  return (
    <div className="pcc-page">
      <div className="pcc-content">
        <div className="pcc-header">
          <h2>Protective Covered Call</h2>
          <p className="pcc-subtitle">
            Deep ITM Covered Call calculator. Uses expirations between {MIN_DTE}+ and {MAX_DTE}- DTE, then selects 3 periods and a call strike closest to 15% ITM.
          </p>
        </div>

        <div className="pcc-controls">
          <label className="pcc-label" htmlFor="pcc-symbol">Ticker</label>
          <input
            id="pcc-symbol"
            type="text"
            className="pcc-input"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder="e.g. AAPL"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={(e) => {
              if (e.key === 'Enter') run();
            }}
          />
          <button type="button" className="pcc-btn" onClick={run} disabled={loading}>
            Calculate
          </button>
          <div className="pcc-meta">
            Target annual return: <strong>{(TARGET_ANNUAL_RETURN * 100).toFixed(0)}%</strong>
          </div>
        </div>

        {error && (
          <div className="pcc-card pcc-error">
            {error}
          </div>
        )}

        {loading && (
          <div className="pcc-card">
            Loading…
          </div>
        )}

        {!loading && raw && (
          <div className="pcc-card">
            <div className="pcc-summary">
              <div>
                Symbol: <strong>{results.symbol || '—'}</strong>
              </div>
              <div>
                Stock price: <strong>{formatMoney(results.stockPrice)}</strong>
              </div>
              <div>
                Highlighted row: closest to <strong>{formatPct(results.targetAnnualPct, 0)}</strong> annualized return
              </div>
            </div>

            <details className="pcc-explain">
              <summary className="pcc-explain-summary">How all fields are calculated</summary>
              <div className="pcc-explain-body">
                <div className="pcc-explain-grid">
                  <div className="pcc-explain-block">
                    <div className="pcc-explain-title">Inputs</div>
                    <div className="pcc-explain-text">
                      <div><strong>Stock price</strong> is the latest underlying mid price returned by the backend.</div>
                      <div><strong>Premium</strong> is the option mid price (credit) per share from marketdata.app.</div>
                      <div><strong>Strike</strong> is the listed strike closest to the protection target for that expiry.</div>
                      <div><strong>DTE</strong> is days until expiration (calendar days).</div>
                    </div>
                  </div>
                  <div className="pcc-explain-block">
                    <div className="pcc-explain-title">Strike selection per expiry</div>
                    <div className="pcc-explain-text">
                      <div>
                        For each selected expiry, we compute three protection targets: <strong>10%</strong>, <strong>15%</strong>, <strong>20%</strong>.
                      </div>
                      <div>
                        For a protection target \(p\%\), the theoretical strike is:
                        <div className="pcc-formula">Desired Strike = Stock Price × (1 − p/100)</div>
                      </div>
                      <div>
                        We choose the <strong>closest listed strike</strong> to that value that still meets the protection target (strike ≤ desired strike). If no strike meets it, we fall back to the closest strike and flag it.
                      </div>
                    </div>
                  </div>
                  <div className="pcc-explain-block">
                    <div className="pcc-explain-title">Core outputs</div>
                    <div className="pcc-explain-text">
                      <div>
                        <strong>Breakeven</strong>:
                        <div className="pcc-formula">Breakeven = Stock Price − Premium</div>
                      </div>
                      <div>
                        <strong>Breakeven and Net Capital Outlay are the same number</strong> in this model because we assume you buy the shares at the current stock price and receive the option premium immediately:
                        <div className="pcc-formula">Net Capital Outlay = Stock Price − Premium = Breakeven</div>
                      </div>
                      <div>
                        <strong>Max Profit (Assigned)</strong> (per share):
                        <div className="pcc-formula">Max Profit = Strike − Net Capital Outlay</div>
                      </div>
                      <div>
                        <strong>Profit % (ROC)</strong>:
                        <div className="pcc-formula">ROC % = (Max Profit / Net Capital Outlay) × 100</div>
                      </div>
                      <div>
                        <strong>Protection %</strong>:
                        <div className="pcc-formula">Protection % = ((Stock Price − Strike) / Stock Price) × 100</div>
                      </div>
                      <div>
                        <strong>Annualized Return %</strong>:
                        <div className="pcc-formula">Annualized % = (ROC % / DTE) × 365</div>
                      </div>
                    </div>
                  </div>
                  <div className="pcc-explain-block">
                    <div className="pcc-explain-title">Risk rules</div>
                    <div className="pcc-explain-text">
                      <div>
                        <strong>High Risk</strong> is shown when <strong>Protection % &lt; 10%</strong>.
                      </div>
                      <div>
                        Rows are highlighted based on closeness to the <strong>Target annual return</strong>, preferring rows that meet their own protection target.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </details>

            {results.rows.length === 0 ? (
              <div className="pcc-empty">
                No expirations available within {MIN_DTE}+ and {MAX_DTE}- DTE from the current option-chain payload.
              </div>
            ) : (
              <div className="pcc-table-wrap">
                <table className="pcc-table">
                  <thead>
                    <tr>
                      <th>Expiry</th>
                      <th>DTE</th>
                      <th>Strike</th>
                      <th>Premium</th>
                      <th className="pcc-th-strong">Breakeven</th>
                      <th>Max Profit (Assigned)</th>
                      <th>Profit % (ROC)</th>
                      <th className="pcc-th-strong">Protection %</th>
                      <th>Annualized Return %</th>
                      <th>Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.rows.map((r, idx) => {
                      const isBest = results.chosenAnnualReturnPct != null && r.annualizedReturnPct != null
                        ? Math.abs(r.annualizedReturnPct - results.chosenAnnualReturnPct) < 1e-9
                        : false;

                      const protectionClass = r.highRisk ? 'pcc-protection-risk' : 'pcc-protection-ok';
                      return (
                        <tr
                          key={`${r.expiration_date}-${r.protectionTargetPct}-${idx}`}
                          className={`${isBest ? 'pcc-row-best' : ''}`}
                        >
                          <td>{r.expiration_date}</td>
                          <td>{r.dte != null ? r.dte : '—'}</td>
                          <td>{r.strike != null ? formatMoney(r.strike) : '—'}</td>
                          <td>{r.premium != null ? formatMoney(r.premium) : '—'}</td>
                          <td className="pcc-strong">{formatMoney(r.breakeven)}</td>
                          <td>{formatMoney(r.maxProfit)}</td>
                          <td>{formatPct(r.rocPct)}</td>
                          <td className={`pcc-strong ${protectionClass}`}>{formatPct(r.protectionPct)}</td>
                          <td>{formatPct(r.annualizedReturnPct)}</td>
                          <td>{r.highRisk ? 'High Risk' : 'OK'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProtectiveCoveredCall;

